// InjurySystem — weekly injury risk, recovery and fatigue decay for the
// player roster. Injured players are unavailable for lineups (leagues,
// tournaments, circuit); the medical staff (doctor + physio) reduces risk
// and accelerates recovery via staffEffects.getMedicalEffects.

import { EventBus, GameEvents } from '../utils/EventBus'
import { useGameStore } from '@/store/gameStore'
import { rollInjury, type Player } from '@/config/playersConfig'
import { getMedicalEffects } from './staffEffects'
import { Rng, defaultRng } from '../utils/rng'

// Weekly injury risk components (probabilities)
export const INJURY_RISK = {
  base: 0.02,
  perYearOver30: 0.005,
  fatigueAtMax: 0.04, // scaled by fatigue/100
  lowForm: 0.01, // form < 35
  cap: 0.12,
} as const

/** Fatigue recovered per week of rest */
export const FATIGUE_RECOVERY_PER_WEEK = 15
/** Form hit when returning from injury (match rust) */
export const RECOVERY_FORM_PENALTY = 10

/** The weekly injury probability for one player (before the medical mult) */
export function baseInjuryRisk(player: Player): number {
  const age = INJURY_RISK.perYearOver30 * Math.max(0, player.age - 30)
  const fatigue = INJURY_RISK.fatigueAtMax * ((player.fatigue ?? 0) / 100)
  const lowForm = player.form < 35 ? INJURY_RISK.lowForm : 0
  return Math.min(INJURY_RISK.cap, INJURY_RISK.base + age + fatigue + lowForm)
}

export class InjurySystem {
  private handler = () => this.processWeek()

  constructor(private rng: Rng = defaultRng) {
    EventBus.on(GameEvents.WEEK_CHANGED, this.handler)
  }

  destroy() {
    EventBus.off(GameEvents.WEEK_CHANGED, this.handler)
  }

  private processWeek() {
    const store = useGameStore.getState()
    if (store.players.length === 0) return

    const medical = getMedicalEffects(store.staff)

    const updated = store.players.map(player => {
      // 1) Recovery: medical staff heals more than 7 days per week
      if (player.injury) {
        const daysRemaining = player.injury.daysRemaining - 7 * medical.recoveryRate
        if (daysRemaining <= 0) {
          EventBus.emit(GameEvents.PLAYER_RECOVERED, {
            playerId: player.id,
            playerName: player.name,
            injuryName: player.injury.name,
          })
          EventBus.emit(GameEvents.NOTIFY, {
            message: `✅ ${player.name} recibe el alta médica (${player.injury.name})`,
            type: 'success',
          })
          return {
            ...player,
            injury: null,
            form: Math.max(0, player.form - RECOVERY_FORM_PENALTY), // rust
            fatigue: 0,
          }
        }
        return { ...player, injury: { ...player.injury, daysRemaining } }
      }

      // 2) Fatigue decay for the healthy (nutritionist speeds it up)
      const fatigue = Math.max(
        0,
        (player.fatigue ?? 0) - FATIGUE_RECOVERY_PER_WEEK - medical.fatigueRecoveryBonus,
      )

      // 3) Risk roll (age, fatigue and poor form raise it; the physio cuts it)
      const risk = baseInjuryRisk(player) * medical.injuryRiskMult
      if (this.rng() < risk) {
        const injury = rollInjury(useGameStore.getState().day, this.rng)
        EventBus.emit(GameEvents.PLAYER_INJURED, {
          playerId: player.id,
          playerName: player.name,
          injuryName: injury.name,
          severity: injury.severity,
          totalDays: injury.totalDays,
        })
        EventBus.emit(GameEvents.NOTIFY, {
          message: `🚑 ${player.name} se lesiona: ${injury.name} (~${injury.totalDays} días)`,
          type: 'error',
        })
        return { ...player, injury, fatigue }
      }

      return fatigue === player.fatigue ? player : { ...player, fatigue }
    })

    useGameStore.getState().setPlayers(updated)
  }
}
